import React, { useState } from 'react';
import { Box, Stack, Typography, Grid } from '@mui/material';
import { Painting } from '../models/Painting';
import PaintingThumbnail from './PaintingThumbnail.tsx';
import FocusedPainting from './FocusedPaintingCard.tsx';

interface PaintingGalleryProps {
  paintings: Painting[];
  title?: string;
  subtitle?: string;
  columns: number;
  rows: number;
  textSize?: number;
}

export default function PaintingGallery({ paintings, title, subtitle, columns, rows, textSize = 1 }: PaintingGalleryProps) {
  const [selectedPainting, setSelectedPainting] = useState<Painting | null>(null);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);

  const handlePaintingClick = (painting: Painting, index: number) => {
    const clickedRow = Math.floor(index / columns);
    
    // If clicking the same painting, close it
    if (selectedPainting === painting) {
      setSelectedPainting(null);
      setSelectedRow(null);
    } else {
      setSelectedPainting(painting);
      setSelectedRow(clickedRow);
    }
  };

  // Create grid items with expanded card insertion
  const createGridItems = (): React.ReactElement[] => {
    const items: React.ReactElement[] = [];
    // Show all paintings regardless of rows parameter
    const visiblePaintings = paintings;

    for (let i = 0; i < visiblePaintings.length; i++) {
      const painting = visiblePaintings[i];
      const currentRow = Math.floor(i / columns);
      const isLastInRow = (i + 1) % columns === 0 || i === visiblePaintings.length - 1;

      // Add the thumbnail
      items.push(
        <Grid item key={`painting-${i}`} xs={12 / columns}>
          <Box
            onClick={() => handlePaintingClick(painting, i)}
            sx={{
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)',
              },
              border: selectedPainting === painting ? '2px solid #61dafb' : 'none',
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          >
            <PaintingThumbnail
              painting={painting}
              setFocusedPainting={() => {}} // We handle clicks in parent
              textSize={textSize}
            />
          </Box>
        </Grid>
      );

      // If this is the last item in the row and there's a selected painting in this row
      if (isLastInRow && selectedPainting && selectedRow === currentRow) {
        items.push(
          <Grid item xs={12} key={`expanded-${currentRow}`}>
            <Box
              sx={{
                mt: 3,
                mb: 3,
                p: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              }}
            >
              <FocusedPainting painting={selectedPainting} textSize={textSize} />
            </Box>
          </Grid>
        );
      }
    }

    return items;
  };

  return (
    <Stack spacing={3}>
      {/* Title Section */}
      {title && (
        <Stack spacing={1}>
          <Typography fontFamily="Playfair Display" fontSize="4vw">
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              align="left" 
              fontFamily="Bitter" 
              fontStyle="italic" 
              fontSize="0.8vw"
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
      )}


      {/* Gallery Grid */}
      <Box sx={{ width: '100%', maxWidth: '80vw' }}>
        <Grid container spacing={2}>
          {createGridItems()}
        </Grid>
      </Box>
    </Stack>
  );
}